'use client';

import { useProfile } from '@/components/providers/profile-provider';
import { useToast } from '@/hooks/use-toast';
import { Check, ChevronRight, Copy } from 'lucide-react';
import Image from 'next/image';
import * as React from 'react';
import { useState } from 'react';
import { PaymentMethodModal } from './payment-method-selector-modal';
import { RedeemedAmountSheet } from './redeemed-amount-sheet';
import { ReferalInfoCard } from './referal-info-card';
import { StatCard } from './stat-card';
import { UserList } from './user-list';
export function ReferralDashboard() {
  const {
    toast
  } = useToast();
  const {
    referrals,
    user
  } = useProfile();
  const [isCopied, setIsCopied] = useState(false);
  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] = useState(false);
  const [isRedeemPageOpen, setIsRedeemPageOpen] = useState(false);
  const [userListType, setUserListType] = useState('Registered users');
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(user?.referralURL as string);
      setIsCopied(true);
      toast({
        description: <div className="flex items-center gap-2">
            <Check className="size-4 text-green-500" />
            <span>Referral link copied to clipboard!</span>
          </div>,
        duration: 2000
      });
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      toast({
        variant: 'destructive',
        description: `Failed to copy link: ${error}`,
        duration: 2000
      });
    }
  };
  const getFilteredUsers = () => {
    if (userListType === 'Enrolled users') {
      return referrals?.referrals.filter(referral => referral.status === 'Enrolled').sort((a, b) => new Date(b.enrolledAt as string).getTime() - new Date(a.enrolledAt as string).getTime());
    }
    return referrals?.referrals.sort((a, b) => new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime());
  };
  return <div className="relative flex w-full flex-col bg-white" data-sentry-component="ReferralDashboard" data-sentry-source-file="referral-dashboard.tsx">
      <div className="flex  w-full flex-1 flex-col items-center gap-2 bg-x-background px-5 pb-5 pt-4">
        <div className="relative  flex w-full flex-col overflow-hidden rounded-lg border border-solid border-black/10 bg-white bg-gradient-to-br from-[#7158e2] to-[#8165e8]  bg-cover bg-center px-4 py-3 shadow-[0px_2px_8px_rgba(0,0,0,0.09)]  md:max-w-[460px]">
          <ReferalInfoCard data-sentry-element="ReferalInfoCard" data-sentry-source-file="referral-dashboard.tsx" />
          <div className="z-0 mt-5 hidden w-full items-center justify-center gap-4 overflow-hidden whitespace-nowrap rounded-md bg-white px-3.5 py-3 text-base font-medium text-x-accent-alpha md:flex">
            <div className="my-auto flex-1 shrink basis-0 self-stretch">
              {user?.referralURL as string}
            </div>
            <button type="button" className="flex items-center transition-colors" aria-label="Copy referral link" onClick={handleCopyLink}>
              {isCopied ? <Check className="size-5 text-green-500" /> : <Copy className="size-5" />}
            </button>
          </div>
        </div>
        <div className="relative mt-2 flex w-full items-center gap-5 overflow-hidden rounded-lg border border-solid border-black/10 bg-white p-5 shadow-[0px_2px_8px_rgba(0,0,0,0.09)] md:max-w-[460px] ">
          <Image height={100} width={100} loading="lazy" src="https://cdn.builder.io/api/v1/image/assets/a38e69b62afa49abbb0ce0d02cadfc7e/e0d93ec3d167e7e83a37d0ec251ae40110cd3cefe743b33155cdf28ce93df2c4?apiKey=a38e69b62afa49abbb0ce0d02cadfc7e&" alt="" className="z-0 my-auto aspect-square w-[50px] shrink-0 self-stretch object-contain" data-sentry-element="Image" data-sentry-source-file="referral-dashboard.tsx" />
          <div className="z-0 my-auto flex flex-1 shrink basis-8 flex-col self-stretch text-zinc-900">
            <div className="text-2xl font-semibold tracking-tight">
              ₹
              {referrals?.overview.totalAmountEarned ?? 0}
            </div>
            <div className="mt-1.5 text-xs">Total amount earned</div>
          </div>
          <button onClick={() => setIsPaymentMethodModalOpen(true)} type="button" className="z-0 my-auto flex items-center justify-center gap-1.5 overflow-hidden rounded-[34px] bg-x-button-background p-1 text-center text-base font-medium text-x-accent-alpha md:whitespace-nowrap md:bg-zinc-900 md:pl-5 md:pr-3.5 md:text-white">
            <span className="hidden self-stretch md:my-auto md:block">Redeem</span>
            <ChevronRight className="size-5" data-sentry-element="ChevronRight" data-sentry-source-file="referral-dashboard.tsx" />
          </button>
        </div>

        <div className="mt-2  hidden max-w-full grid-cols-2 grid-rows-1 items-start gap-1.5 rounded-md text-zinc-900 md:grid md:w-[460px]">
          <StatCard value={`₹${referrals?.wallet.totalAmountWithdrawn ?? 0} `} label="Redeemed amounts" icon={!!referrals?.wallet.totalAmountWithdrawn && referrals?.wallet.totalAmountWithdrawn > 0} className="h-[100px]" onClick={() => !!referrals?.wallet.totalAmountWithdrawn && referrals?.wallet.totalAmountWithdrawn > 0 ? setIsRedeemPageOpen(true) : null} data-sentry-element="StatCard" data-sentry-source-file="referral-dashboard.tsx" />
          <StatCard value={`₹${referrals?.wallet.availableBalance ?? 0} `} className="h-[100px]" label="Wallet balance" data-sentry-element="StatCard" data-sentry-source-file="referral-dashboard.tsx" />
          <StatCard value={referrals?.overview.totalRegisteredStudents ?? 0} label="Total Registered" className="h-[100px]" data-sentry-element="StatCard" data-sentry-source-file="referral-dashboard.tsx" />
          <StatCard value={referrals?.overview.totalEnrolledStudents ?? 0} label="Total Enrolled" className="h-[100px]" data-sentry-element="StatCard" data-sentry-source-file="referral-dashboard.tsx" />
        </div>
        <UserList users={getFilteredUsers()} className="mb-20" type={userListType} selectedType={userListType} setSelectedType={type => setUserListType(type)} data-sentry-element="UserList" data-sentry-source-file="referral-dashboard.tsx" />
        <RedeemedAmountSheet open={isRedeemPageOpen} onOpenChange={setIsRedeemPageOpen} data-sentry-element="RedeemedAmountSheet" data-sentry-source-file="referral-dashboard.tsx" />
      </div>
      <PaymentMethodModal open={isPaymentMethodModalOpen} onOpenChange={setIsPaymentMethodModalOpen} redeem data-sentry-element="PaymentMethodModal" data-sentry-source-file="referral-dashboard.tsx" />

    </div>;
}
;